import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Layout from "@components/layout/Layout";
import InfoPanel from "@components/form/InfoPanel";
import Loading from "@components/shared/Loading";
import { useAppContext } from "@context/AppContext";
import { useReportSnapshot } from "@hooks/query/useReportSnapshot";
import {
  formatReportPeriodDate,
  formatReportGeneratedAt,
} from "@components/reports/adhoc/formatCell";
import { useExportReportRows } from "@components/reports/adhoc/useExportReportRows";
import ReportResultView from "@components/reports/adhoc/ReportResultView";
import ReportingWorkspace from "@components/reports/adhoc/ReportingWorkspace";

const PAGE_SIZE = 100;

/**
 * The read-only snapshot view, presented as DEQ presents its saved report
 * history: the same screen the report ran on, minus everything that would
 * change it — no dates, no edit links, no save actions. The rows are the
 * stored payload; export offers the same CSV/PDF dialog as a live run.
 */
const ReportSnapshotView: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { checkPermissions } = useAppContext();
  const { data: snapshot, isLoading, isError, refetch } = useReportSnapshot(id);
  const [selectedRecordIds, setSelectedRecordIds] = useState<any[]>([]);
  const canGenerate = checkPermissions(["GenerateReports"]);
  const resultFrom =
    snapshot?.data?.dateRange?.from ?? snapshot?.fromDate ?? "";
  const resultTo = snapshot?.data?.dateRange?.to ?? snapshot?.toDate ?? "";

  const rows = useMemo(
    () =>
      (snapshot?.data?.rows ?? []).map(
        (row, index): Record<string, any> => ({
          ...row,
          _rowId: row._rowId ?? index,
        }),
      ),
    [snapshot],
  );

  const handleSelectionChange = useCallback(
    (ids: any[]) => setSelectedRecordIds(ids.map(Number)),
    [],
  );

  const selectedRows = useMemo(() => {
    const selectedIds = new Set(selectedRecordIds.map(Number));
    return rows.filter(row => selectedIds.has(Number(row._rowId)));
  }, [rows, selectedRecordIds]);

  const { exportDialog, openExportDialog, isExporting } = useExportReportRows({
    baseFileName: snapshot?.name ?? (t("reporting.ad_hoc_report") as string),
    columns: snapshot?.data?.columns ?? [],
    rows,
    selectedRows,
    reportSnapshotId: snapshot?.id,
  });

  const infoPanel = snapshot ? (
    <InfoPanel heading={t("reporting.snapshot_read_only_badge") as string}>
      <div className="space-y-4">
        <p>
          {t("reporting.snapshot_view_info1", {
            createdAt: formatReportGeneratedAt(snapshot.createdAt),
            by: snapshot.generatedBy?.name ?? "-",
            from: formatReportPeriodDate(resultFrom),
            to: formatReportPeriodDate(resultTo),
          })}
        </p>
        {snapshot.data?.level1ResourceName ? (
          <p>
            {t("reporting.snapshot_view_info_scheme", {
              name: snapshot.data.level1ResourceName,
            })}
          </p>
        ) : null}
        {snapshot.data?.accountingPeriod ? (
          <p>
            {t("reporting.snapshot_view_info_accounting_period", {
              from: formatReportPeriodDate(
                snapshot.data.accountingPeriod.periodStart,
              ),
              to: formatReportPeriodDate(
                snapshot.data.accountingPeriod.periodEnd,
              ),
            })}
          </p>
        ) : null}
        {snapshot.reportTemplate ? (
          <p>
            {t("reporting.snapshot_view_info_template", {
              name: snapshot.reportTemplate.name,
            })}
            {snapshot.reportTemplate.isDeleted
              ? ` ${t("reporting.template_deleted_marker")}`
              : null}
          </p>
        ) : null}
        <p>{t("reporting.snapshot_view_info2")}</p>
        {canGenerate ? <p>{t("reporting.snapshot_view_info3")}</p> : null}
      </div>
    </InfoPanel>
  ) : undefined;

  const footer = (
    <footer className="mt-auto flex gap-4 border-t border-gray-200 p-6">
      <button
        type="button"
        className="btn-outline-primary"
        onClick={() => navigate("/polestar/reports/adhoc#1")}
      >
        {t("common.back")}
      </button>
    </footer>
  );

  return (
    <Layout
      permissions={["ViewReports"]}
      breadcrumb={[
        { label: t("dashboard.dashboard"), href: "/polestar" },
        {
          label: t("reporting.hub_title"),
          href: "/polestar/reports/adhoc#1",
        },
        { label: t("reporting.history_view_page_title") },
      ]}
      title={snapshot?.name ?? t("reporting.history_title")}
      status={
        snapshot ? (
          <span className="rounded-full bg-gray-200 px-2.5 py-1 text-xs font-semibold text-gray-700">
            {t("reporting.snapshot_read_only_badge")}
          </span>
        ) : undefined
      }
    >
      <ReportingWorkspace activeSection="history" infoPanel={infoPanel}>
        {isLoading ? (
          <div className="grow p-4">
            <Loading />
          </div>
        ) : isError ? (
          <div className="grow p-4 text-sm text-red-700">
            <p>{t("reporting.history_load_failed")}</p>
            <button
              type="button"
              className="btn-default mt-3"
              onClick={() => refetch()}
            >
              {t("common.refresh")}
            </button>
          </div>
        ) : snapshot ? (
          <ReportResultView
            actions={
              canGenerate ? (
                <button
                  type="button"
                  className="btn-primary"
                  disabled={isExporting || rows.length === 0}
                  onClick={openExportDialog}
                >
                  {t("reporting.export_data")}
                </button>
              ) : undefined
            }
            metadata={
              <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                <span>
                  {snapshot.data?.level1ResourceName ??
                    t("reporting.all_schemes")}
                </span>
                <span>
                  {formatReportPeriodDate(resultFrom)} -{" "}
                  {formatReportPeriodDate(resultTo)}
                </span>
                {snapshot.data?.accountingPeriod ? (
                  <span>
                    {t("reporting.water_year_summary", {
                      from: formatReportPeriodDate(
                        snapshot.data.accountingPeriod.periodStart,
                      ),
                      to: formatReportPeriodDate(
                        snapshot.data.accountingPeriod.periodEnd,
                      ),
                    })}
                  </span>
                ) : null}
                <span>
                  {t("reporting.total_rows", {
                    total: snapshot.data?.total ?? rows.length,
                  })}
                </span>
                <span>
                  {t("reporting.selected_rows", {
                    count: selectedRecordIds.length,
                  })}
                </span>
              </div>
            }
            columns={snapshot.data?.columns ?? []}
            rows={rows}
            onSelectionChange={handleSelectionChange}
            tableKey={snapshot.id}
            pageSize={PAGE_SIZE}
            footer={footer}
          />
        ) : (
          <>
            <p className="grow p-4 text-gray-500">
              {t("reporting.snapshot_missing")}
            </p>
            {footer}
          </>
        )}
      </ReportingWorkspace>

      {exportDialog}
    </Layout>
  );
};

export default ReportSnapshotView;
